import React, { useState, useEffect } from 'react';
import { Configuration } from '../BackendApiClient/runtime';
import { 
  RegionsApi, 
  DeviceInfosApi, 
  ClassifiersApi,
  PersonasApi, 
  PersonaGroupsApi,
  TasksApi,
  ProxiesApi,
  BotsApi,
  NarrativesApi,
  ExperimentsApi
} from '../BackendApiClient/apis';
import {
  RegionResponse,
  DeviceInfoResponse,
  PersonaResponse,
  ClassifierResponse,
  PersonaGroupResponse,
  ProxyResponse,
  InputTask,
  TaskType,
  PersonaType,
  NarrativeResponse,
  ExperimentResponse
} from '../BackendApiClient/models';
import { countryCodeToFlag, getProxyDisplayName } from '../utils/proxyDisplay';
import './BotGroupWizard.css';

interface WizardStep {
  id: number;
  title: string;
  description: string;
}

interface ScheduledTask {
  task_id: number;
  min_duration_seconds?: number;
  max_duration_seconds?: number;
  duration_seconds?: number;
  total_duration_seconds?: number;
  number_of_tasks: number;
}

interface ScheduledBlock {
  scheduled_tasks: ScheduledTask[];
  block_duration_seconds: number;
  tasks_order_type: 'sequential' | 'random';
}

interface ScheduleGroup {
  scheduled_blocks: ScheduledBlock[];
  total_duration_seconds: number;
  blocks_order_type: 'sequential' | 'random';
  variance_percentage: number;
  use_queue?: boolean;
}

interface TaskResponse {
  task_id: number;
  task_type: string;
  task_params: any;
  record_creation_time: string;
}

interface TimeSlot {
  type: 'sleep' | 'activity';
  startOffsetSeconds: number;
  endOffsetSeconds: number;
  durationSeconds: number;
  localStart: string;
  localEnd: string;
  sleepTaskId?: number;
  activityBlocks: ScheduledBlock[];
}

const STEPS: WizardStep[] = [
  { id: 1, title: 'Region', description: 'Select or create a region' },
  { id: 2, title: 'Device', description: 'Choose device configuration' },
  { id: 3, title: 'Personas', description: 'Create bot personas' },
  { id: 4, title: 'Classifier', description: 'Assign content classifier' },
  { id: 5, title: 'Tasks', description: 'Configure bot behavior' },
  { id: 6, title: 'Review', description: 'Review and deploy' }
];

const DAY_SECONDS = 24 * 60 * 60;
const DEFAULT_MIN_TASK_SECONDS = 5 * 60;
const DEFAULT_MAX_TASK_SECONDS = 30 * 60;
const DEFAULT_SLEEP_MIN_SECONDS = 2 * 60;
const DEFAULT_SLEEP_MAX_SECONDS = 90 * 60;

const createSleepTaskResponse = (taskId: number): TaskResponse => ({
  task_id: taskId,
  task_type: 'sleep',
  task_params: {},
  record_creation_time: '',
});

const findSleepTask = (tasks: TaskResponse[], sleepTaskId?: number) =>
  (sleepTaskId ? tasks.find(t => t.task_id === sleepTaskId) : undefined) ||
  tasks.find(t => t.task_type === 'sleep') ||
  (sleepTaskId ? createSleepTaskResponse(sleepTaskId) : undefined);

const createDefaultTask = (task: TaskResponse): ScheduledTask => ({
  task_id: task.task_id,
  min_duration_seconds: DEFAULT_MIN_TASK_SECONDS,
  max_duration_seconds: DEFAULT_MAX_TASK_SECONDS,
  number_of_tasks: 1,
});

const createFixedTask = (task: TaskResponse, durationSeconds: number): ScheduledTask => ({
  task_id: task.task_id,
  duration_seconds: durationSeconds,
  number_of_tasks: 1,
});

const createQuotaTask = (
  task: TaskResponse,
  totalDurationSeconds: number,
  minDurationSeconds: number,
  maxDurationSeconds: number,
): ScheduledTask => ({
  task_id: task.task_id,
  min_duration_seconds: Math.min(minDurationSeconds, totalDurationSeconds),
  max_duration_seconds: Math.min(maxDurationSeconds, totalDurationSeconds),
  total_duration_seconds: totalDurationSeconds,
  number_of_tasks: 1,
});

const createRandomMixBlock = (
  blockDurationSeconds: number,
  tasks: TaskResponse[],
  sleepTaskId?: number,
): ScheduledBlock | null => {
  const sleepTask = findSleepTask(tasks, sleepTaskId);
  const forYouTask = tasks.find(t => t.task_type === 'for_you_feed');
  const scheduled_tasks: ScheduledTask[] = [];

  if (sleepTask && forYouTask) {
    scheduled_tasks.push(createDefaultTask(sleepTask));
    scheduled_tasks.push(createDefaultTask(forYouTask));
  } else {
    const nonSleep = tasks.filter(t => t.task_type !== 'sleep');
    const defaultTask = forYouTask || nonSleep[0] || sleepTask || tasks[0];
    if (!defaultTask) return null;
    scheduled_tasks.push(createDefaultTask(defaultTask));
  }

  return {
    scheduled_tasks,
    block_duration_seconds: blockDurationSeconds,
    tasks_order_type: sleepTask && forYouTask ? 'random' : 'sequential',
  };
};

const createHumanBlocksForDuration = (
  durationSeconds: number,
  tikTokSeconds: number,
  sleepTask: TaskResponse | undefined,
  forYouTask: TaskResponse | undefined,
): ScheduledBlock[] => {
  const clampedTikTokSeconds = Math.min(durationSeconds, Math.max(0, tikTokSeconds));
  const sleepSeconds = durationSeconds - clampedTikTokSeconds;
  const scheduled_tasks: ScheduledTask[] = [];

  if (sleepTask && sleepSeconds > 0) {
    scheduled_tasks.push(createQuotaTask(
      sleepTask,
      sleepSeconds,
      DEFAULT_SLEEP_MIN_SECONDS,
      DEFAULT_SLEEP_MAX_SECONDS,
    ));
  }

  if (forYouTask && clampedTikTokSeconds > 0) {
    scheduled_tasks.push(createQuotaTask(
      forYouTask,
      clampedTikTokSeconds,
      DEFAULT_MIN_TASK_SECONDS,
      DEFAULT_MAX_TASK_SECONDS,
    ));
  }

  return scheduled_tasks.length > 0
    ? [{
        scheduled_tasks,
        block_duration_seconds: durationSeconds,
        tasks_order_type: 'random',
      }]
    : [];
};

const autofillActivityBlocks = (
  slots: TimeSlot[],
  tasks: TaskResponse[],
  sleepTaskId: number | undefined,
  researchMode: boolean,
  dailyTikTokHours: number,
): TimeSlot[] => {
  const activitySecondsByDay = new Map<number, number>();
  const activitySegmentsByDay = new Map<number, Array<{ start: number; end: number }>>();
  const usedTikTokSecondsByDay = new Map<number, number>();
  const dailyTikTokBudgetSeconds = Math.max(0, Math.round(dailyTikTokHours * 60 * 60));
  const sleepTask = findSleepTask(tasks, sleepTaskId);
  const forYouTask = tasks.find(t => t.task_type === 'for_you_feed');

  slots.forEach(slot => {
    if (slot.type !== 'activity') return;

    let cursor = slot.startOffsetSeconds;
    while (cursor < slot.endOffsetSeconds) {
      const dayIndex = Math.floor(cursor / DAY_SECONDS);
      const dayEnd = Math.min(slot.endOffsetSeconds, (dayIndex + 1) * DAY_SECONDS);
      activitySecondsByDay.set(
        dayIndex,
        (activitySecondsByDay.get(dayIndex) || 0) + dayEnd - cursor,
      );
      activitySegmentsByDay.set(
        dayIndex,
        [...(activitySegmentsByDay.get(dayIndex) || []), { start: cursor, end: dayEnd }],
      );
      cursor = dayEnd;
    }
  });

  const remainingActivityFrom = (dayIndex: number, offsetSeconds: number) =>
    (activitySegmentsByDay.get(dayIndex) || []).reduce((sum, segment) => {
      if (segment.end <= offsetSeconds) return sum;
      return sum + segment.end - Math.max(segment.start, offsetSeconds);
    }, 0);

  return slots.map(slot => {
    if (slot.type !== 'activity') return slot;

    const activityBlocks = researchMode
      ? (() => {
          const block = createRandomMixBlock(slot.durationSeconds, tasks, sleepTaskId);
          return block ? [block] : [];
        })()
      : (() => {
          const blocks: ScheduledBlock[] = [];
          let cursor = slot.startOffsetSeconds;

          while (cursor < slot.endOffsetSeconds) {
            const dayIndex = Math.floor(cursor / DAY_SECONDS);
            const dayEnd = Math.min(slot.endOffsetSeconds, (dayIndex + 1) * DAY_SECONDS);
            const chunkDuration = dayEnd - cursor;
            const dayActivitySeconds = activitySecondsByDay.get(dayIndex) || chunkDuration;
            const dayTikTokBudget = Math.min(dailyTikTokBudgetSeconds, dayActivitySeconds);
            const usedToday = usedTikTokSecondsByDay.get(dayIndex) || 0;
            const remainingTikTokToday = Math.max(0, dayTikTokBudget - usedToday);
            const remainingActivityToday = Math.max(1, remainingActivityFrom(dayIndex, cursor));
            const isLastChunkOfDay = remainingActivityFrom(dayIndex, dayEnd) <= 0;
            const tikTokForChunk = isLastChunkOfDay
              ? Math.min(chunkDuration, remainingTikTokToday)
              : Math.min(
                  chunkDuration,
                  remainingTikTokToday,
                  Math.round((remainingTikTokToday * chunkDuration) / remainingActivityToday),
                );

            blocks.push(...createHumanBlocksForDuration(chunkDuration, tikTokForChunk, sleepTask, forYouTask));
            usedTikTokSecondsByDay.set(dayIndex, usedToday + tikTokForChunk);
            cursor = dayEnd;
          }

          return blocks;
        })();

    return {
      ...slot,
      activityBlocks,
    };
  });
};

const BotGroupWizard: React.FC = () => {
  const [currentStep, setCurrentStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // API clients
  const apiConfig = new Configuration({ basePath: '' });
  const regionsApi = new RegionsApi(apiConfig);
  const devicesApi = new DeviceInfosApi(apiConfig);
  const classifiersApi = new ClassifiersApi(apiConfig);
  const personasApi = new PersonasApi(apiConfig);
  const personaGroupsApi = new PersonaGroupsApi(apiConfig);
  const tasksApi = new TasksApi(apiConfig);
  const proxiesApi = new ProxiesApi(apiConfig);
  const botsApi = new BotsApi(apiConfig);
  const narrativesApi = new NarrativesApi(apiConfig);
  const experimentsApi = new ExperimentsApi(apiConfig);

  // Available options
  const [regions, setRegions] = useState<RegionResponse[]>([]);
  const [devices, setDevices] = useState<DeviceInfoResponse[]>([]);
  const [classifiers, setClassifiers] = useState<ClassifierResponse[]>([]);
  const [proxies, setProxies] = useState<ProxyResponse[]>([]);
  const [narratives, setNarratives] = useState<NarrativeResponse[]>([]);
  const [experiments, setExperiments] = useState<ExperimentResponse[]>([]);

  // Random interest labels pool
  const INTEREST_LABELS = [
    'Gaming', 'Cooking', 'Travel', 'Fashion', 'Music', 'Sports', 'Art', 'Technology',
    'Fitness', 'Photography', 'Reading', 'Dancing', 'Comedy', 'Nature', 'Cars', 'Beauty',
    'Food', 'Pets', 'DIY', 'Science', 'Movies', 'Anime', 'Yoga', 'Coffee', 'Design',
    'Hiking', 'Shopping', 'Gardening', 'Writing', 'Vlogging', 'Streaming', 'Meditation',
    'Basketball', 'Soccer', 'Tennis', 'Running', 'Cycling', 'Skateboarding', 'Surfing',
    'Makeup', 'Skincare', 'Hairstyle', 'Nails', 'Tattoos', 'Jewelry', 'Vintage', 'Luxury'
  ];

  interface PersonaConfig {
    demographic: string;
    labels: string[];
    proxyId: number | null;
  }

  const WIZARD_STORAGE_KEY = 'botGroupWizard_savedOptions';

  const getSavedOptions = () => {
    try {
      const saved = localStorage.getItem(WIZARD_STORAGE_KEY);
      return saved ? JSON.parse(saved) : null;
    } catch { return null; }
  };

  const savedOptions = getSavedOptions();

  // Wizard state (restored from localStorage when available)
  const [selectedRegions, setSelectedRegions] = useState<RegionResponse[]>([]);
  const [selectedDevices, setSelectedDevices] = useState<DeviceInfoResponse[]>([]);
  const [personasToCreate, setPersonasToCreate] = useState<number>(savedOptions?.personasToCreate ?? 5);
  const [personaConfigs, setPersonaConfigs] = useState<PersonaConfig[]>([]);
  const [proxyConfig, setProxyConfig] = useState(savedOptions?.proxyConfig ?? { 
    enabled: false, 
    proxyId: null as number | null, 
    newProxyUrl: '',
    createMode: 'url' as 'url' | 'brightdata',
    brightDataProxyName: '',
    brightDataCountry: '',
    brightDataIpCount: 1
  });
  const [ispCountries, setIspCountries] = useState<{ [key: string]: string | null }>({});
  const [loadingCountries, setLoadingCountries] = useState(false);
  const [countrySearch, setCountrySearch] = useState('');
  const [countryPickerOpen, setCountryPickerOpen] = useState(false);
  const [selectedClassifier, setSelectedClassifier] = useState<ClassifierResponse | null>(null);
  const [selectedNarratives, setSelectedNarratives] = useState<number[]>(savedOptions?.selectedNarrativeIds ?? []);
  const [experimentMode, setExperimentMode] = useState<'new' | 'existing'>(savedOptions?.experimentMode ?? 'new');
  const [selectedExperiment, setSelectedExperiment] = useState<ExperimentResponse | null>(null);
  const [groupName, setGroupName] = useState('');
  const [experimentName, setExperimentName] = useState('');
  const [availableTasks, setAvailableTasks] = useState<TaskResponse[]>([]);
  const [scheduleBlocks, setScheduleBlocks] = useState<ScheduledBlock[]>([{
    scheduled_tasks: [],
    block_duration_seconds: 7200,
    tasks_order_type: 'sequential'
  }]);
  const [blocksOrderType, setBlocksOrderType] = useState<'sequential' | 'random'>(savedOptions?.blocksOrderType ?? 'sequential');
  const [variance, setVariance] = useState(savedOptions?.variance ?? 20);
  const [useQueue, setUseQueue] = useState(savedOptions?.useQueue ?? false);
  const [totalDuration, setTotalDuration] = useState(savedOptions?.totalDuration ?? 86400);
  const [defaultBlockDuration, setDefaultBlockDuration] = useState(savedOptions?.defaultBlockDuration ?? 120);
  
  // Sleep scheduling
  const [useSleepSchedule, setUseSleepSchedule] = useState(savedOptions?.useSleepSchedule ?? true);
  const [totalDays, setTotalDays] = useState(savedOptions?.totalDays ?? 3);
  const [sleepStartHour, setSleepStartHour] = useState(savedOptions?.sleepStartHour ?? 0);
  const [sleepEndHour, setSleepEndHour] = useState(savedOptions?.sleepEndHour ?? 7);
  const [sleepVariance, setSleepVariance] = useState(savedOptions?.sleepVariance ?? 30);
  const [researchMode, setResearchMode] = useState(savedOptions?.researchMode ?? false);
  const [dailyTikTokHours, setDailyTikTokHours] = useState(savedOptions?.dailyTikTokHours ?? 3);
  const [timeSlots, setTimeSlots] = useState<TimeSlot[]>([]);
  const [generatingSleep, setGeneratingSleep] = useState(false);

  // New region/device forms
  const [showNewRegionForm, setShowNewRegionForm] = useState(false);
  const [showNewDeviceForm, setShowNewDeviceForm] = useState(false);
  const [newRegion, setNewRegion] = useState({
    carrier_region: '',
    mcc_mnc: '',
    locale: '',
    timezone_name: '',
    carrier_name: '',
    timezone_long_name: ''
  });
  const [newDevice, setNewDevice] = useState({
    device_brand: '',
    device_platform: '',
    device_type: '',
    device_os_api: 34,
    device_os_version: 13,
    resolution_width: 1080,
    resolution_height: 2400,
    dpi: 440,
    cpu_abi: '',
    web_ua: ''
  });

  // Load initial data
  useEffect(() => {
    loadData();
  }, []);

  // Load tasks when reaching step 5
  useEffect(() => {
    if (currentStep === 5 && availableTasks.length === 0) {
      loadTasks();
    }
  }, [currentStep]);

  const loadTasks = async () => {
    try {
      const response = await fetch('/api/tasks?time_back_seconds=2592000'); // 30 days
      if (!response.ok) throw new Error('Failed to load tasks');
      const tasks = await response.json();
      setAvailableTasks(tasks);
    } catch (err) {
      console.error('Failed to load tasks:', err);
    }
  };

  const loadData = async () => {
    try {
      const [regionsData, devicesData, classifiersData, proxiesData, narrativesData, experimentsData] = await Promise.all([
        regionsApi.getRegionsApiRegionsGet(),
        devicesApi.getDeviceInfosApiDeviceInfosGet(),
        classifiersApi.getClassifiersApiClassifiersGet(),
        proxiesApi.getProxiesApiProxiesGet(),
        narrativesApi.listNarrativesApiNarrativesGet(),
        experimentsApi.listExperimentsApiExperimentsGet()
      ]);
      const regionsArr = regionsData as RegionResponse[];
      const devicesArr = devicesData as DeviceInfoResponse[];
      const classifiersArr = classifiersData as ClassifierResponse[];
      const experimentsArr = experimentsData as ExperimentResponse[];
      setRegions(regionsArr);
      setDevices(devicesArr);
      setClassifiers(classifiersArr);
      setProxies(proxiesData as ProxyResponse[]);
      setNarratives(narrativesData as NarrativeResponse[]);
      setExperiments(experimentsArr);

      // Restore selections from localStorage after data is loaded
      const saved = getSavedOptions();
      if (saved) {
        if (saved.selectedRegionIds?.length) {
          const restored = regionsArr.filter(r => saved.selectedRegionIds.includes(r.regionId));
          if (restored.length > 0) setSelectedRegions(restored);
        }
        if (saved.selectedDeviceIds?.length) {
          const restored = devicesArr.filter(d => saved.selectedDeviceIds.includes(d.deviceInfoId));
          if (restored.length > 0) setSelectedDevices(restored);
        }
        if (saved.selectedClassifierId != null) {
          const restored = classifiersArr.find(c => c.classifierId === saved.selectedClassifierId);
          if (restored) setSelectedClassifier(restored);
        }
        if (saved.selectedExperimentId != null) {
          const restored = experimentsArr.find(e => e.experimentId === saved.selectedExperimentId);
          if (restored) setSelectedExperiment(restored);
        }
      }
    } catch (err) {
      console.error('Failed to load data:', err);
      setError('Failed to load initial data');
    }
  };

  const loadIspCountries = async () => {
    setLoadingCountries(true);
    try {
      const countries = await proxiesApi.getBrightDataIspCountriesApiProxiesIspCountriesGet({});
      setIspCountries(countries);
    } catch (err) {
      console.error('Failed to load ISP countries:', err);
    } finally {
      setLoadingCountries(false);
    }
  };

  // Format duration helper
  const formatDuration = (seconds: number) => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    if (hours >= 24) {
      const days = Math.floor(hours / 24);
      const remHours = hours % 24;
      return `${days}d ${remHours}h`;
    }
    return hours > 0 ? `${hours}h ${minutes}m` : `${minutes}m`;
  };

  const isForYouTaskId = (taskId: number) =>
    availableTasks.some(t => t.task_id === taskId && t.task_type === 'for_you_feed');

  const getAllocatedForYouSecondsForDay = (dayIndex: number) => timeSlots.reduce((slotSum, currentSlot) => {
    if (currentSlot.type !== 'activity' || Math.floor(currentSlot.startOffsetSeconds / DAY_SECONDS) !== dayIndex) {
      return slotSum;
    }
    return slotSum + currentSlot.activityBlocks.reduce((blockSum, block) => blockSum + block.scheduled_tasks.reduce(
      (taskSum, task) => taskSum + (isForYouTaskId(task.task_id)
        ? (task.total_duration_seconds ?? block.block_duration_seconds)
        : 0),
      0,
    ), 0);
  }, 0);


  // Generate sleep schedule based on selected region
  const generateSleepSchedule = async () => {
    if (selectedRegions.length === 0) {
      alert('Please select at least one region first');
      return;
    }
    
    // Use first selected region for timezone
    const region = selectedRegions[0];
    
    setGeneratingSleep(true);
    try {
      const totalSeconds = totalDays * 24 * 60 * 60;
      const response = await tasksApi.generateSleepBlocksApiTasksGenerateSleepBlocksPost({
        sleepScheduleRequest: {
          regionId: region.regionId,
          totalDurationSeconds: totalSeconds,
          sleepStartHour,
          sleepEndHour,
          varianceMinutes: sleepVariance,
        }
      });
      
      const sleepPeriods = (response.sleepPeriods || []).map((p: any) => ({
        startOffsetSeconds: p.startOffsetSeconds,
        endOffsetSeconds: p.endOffsetSeconds,
        localStart: p.localStart,
        localEnd: p.localEnd,
      }));

      const sleepTaskId = response.blocks?.[0]?.scheduledTasks?.[0]?.taskId;
      let tasksForAutofill = availableTasks;
      if (sleepTaskId && !tasksForAutofill.some(t => t.task_id === sleepTaskId)) {
        tasksForAutofill = [...tasksForAutofill, createSleepTaskResponse(sleepTaskId)];
        setAvailableTasks(tasksForAutofill);
      }
      
      // Calculate schedule end time based on first sleep start + total duration
      // Use region timezone from API response for consistency
      const parseLocalTime = (timeStr: string) => timeStr.replace(/^\d{4}-/, ''); // "2025-12-24 15:00" -> "12-24 15:00"
      
      // Build time slots - derive activity times from sleep boundaries
      const slots: TimeSlot[] = [];
      let currentOffset = 0;

      for (let i = 0; i < sleepPeriods.length; i++) {
        const sleep = sleepPeriods[i];
        
        if (sleep.startOffsetSeconds > currentOffset) {
          // Activity slot before this sleep
          const activityStart = i === 0 ? 'Now' : parseLocalTime(sleepPeriods[i-1].localEnd);
          slots.push({
            type: 'activity',
            startOffsetSeconds: currentOffset,
            endOffsetSeconds: sleep.startOffsetSeconds,
            durationSeconds: sleep.startOffsetSeconds - currentOffset,
            localStart: activityStart,
            localEnd: parseLocalTime(sleep.localStart),
            activityBlocks: [],
          });
        }
        
        slots.push({
          type: 'sleep',
          startOffsetSeconds: sleep.startOffsetSeconds,
          endOffsetSeconds: sleep.endOffsetSeconds,
          durationSeconds: sleep.endOffsetSeconds - sleep.startOffsetSeconds,
          localStart: parseLocalTime(sleep.localStart),
          localEnd: parseLocalTime(sleep.localEnd),
          sleepTaskId,
          activityBlocks: [],
        });
        
        currentOffset = sleep.endOffsetSeconds;
      }
      
      if (currentOffset < totalSeconds) {
        const lastSleepEnd = sleepPeriods.length > 0 ? 
          parseLocalTime(sleepPeriods[sleepPeriods.length - 1].localEnd) : 'Now';
        // Calculate end time by adding remaining duration to last sleep end
        const remainingHours = Math.floor((totalSeconds - currentOffset) / 3600);
        const endTimeNote = `+${remainingHours}h`;
        slots.push({
          type: 'activity',
          startOffsetSeconds: currentOffset,
          endOffsetSeconds: totalSeconds,
          durationSeconds: totalSeconds - currentOffset,
          localStart: lastSleepEnd,
          localEnd: endTimeNote,
          activityBlocks: [],
        });
      }
      
      setTimeSlots(autofillActivityBlocks(slots, tasksForAutofill, sleepTaskId, researchMode, dailyTikTokHours));
      setTotalDuration(totalSeconds);
    } catch (err) {
      console.error('Failed to generate sleep schedule:', err);
      alert('Failed to generate sleep schedule');
    } finally {
      setGeneratingSleep(false);
    }
  };

  // Add block to time slot
  const addBlockToSlot = (slotIndex: number) => {
    if (!availableTasks.length) {
      alert('No tasks available');
      return;
    }
    const slot = timeSlots[slotIndex];
    if (slot.type === 'sleep') return;
    
    // Find sleep and for_you tasks
    const sleepTask = availableTasks.find(t => t.task_type === 'sleep');
    const forYouTask = availableTasks.find(t => t.task_type === 'for_you_feed');
    const hasBothSleepAndForYou = sleepTask && forYouTask;
    
    // Calculate remaining time
    const alreadyScheduled = slot.activityBlocks.reduce((sum, b) => sum + b.block_duration_seconds, 0);
    const remainingTime = slot.durationSeconds - alreadyScheduled;
    
    // First block fills entire slot, subsequent use remaining or default
    const blockDurationSeconds = slot.activityBlocks.length === 0 
      ? slot.durationSeconds  
      : Math.min(remainingTime, defaultBlockDuration * 60);
    
    if (blockDurationSeconds <= 0) {
      alert('No remaining time in this activity slot.');
      return;
    }
    
    // Build scheduled tasks - if both sleep and for_you exist, add both
    const scheduled_tasks: ScheduledTask[] = [];
    if (hasBothSleepAndForYou) {
      const dayIndex = Math.floor(slot.startOffsetSeconds / DAY_SECONDS);
      const allocatedForYouSeconds = getAllocatedForYouSecondsForDay(dayIndex);
      const remainingDailySeconds = Math.max(0, Math.round(dailyTikTokHours * 60 * 60) - allocatedForYouSeconds);
      const forYouTotalSeconds = remainingDailySeconds >= DEFAULT_MIN_TASK_SECONDS
        ? Math.min(blockDurationSeconds, remainingDailySeconds)
        : 0;
      const sleepTotalSeconds = blockDurationSeconds - forYouTotalSeconds;
      if (sleepTotalSeconds > 0) {
        scheduled_tasks.push(createQuotaTask(
          sleepTask,
          sleepTotalSeconds,
          DEFAULT_SLEEP_MIN_SECONDS,
          DEFAULT_SLEEP_MAX_SECONDS,
        ));
      }
      if (forYouTotalSeconds > 0) {
        scheduled_tasks.push(createQuotaTask(
          forYouTask,
          forYouTotalSeconds,
          DEFAULT_MIN_TASK_SECONDS,
          DEFAULT_MAX_TASK_SECONDS,
        ));
      }
    } else {
      // Fall back to first non-sleep task
      const nonSleep = availableTasks.filter(t => t.task_type !== 'sleep');
      const defaultTask = nonSleep.length > 0 ? nonSleep[0] : availableTasks[0];
      scheduled_tasks.push({
        task_id: defaultTask.task_id,
        min_duration_seconds: 300,
        max_duration_seconds: 1800,
        number_of_tasks: 1
      });
    }
    
    const newSlots = [...timeSlots];
    newSlots[slotIndex].activityBlocks.push({
      scheduled_tasks,
      block_duration_seconds: blockDurationSeconds,
      tasks_order_type: hasBothSleepAndForYou ? 'random' : 'sequential'
    });
    setTimeSlots(newSlots);
  };

  const removeBlockFromSlot = (slotIndex: number, blockIndex: number) => {
    const newSlots = [...timeSlots];
    newSlots[slotIndex].activityBlocks.splice(blockIndex, 1);
    setTimeSlots(newSlots);
  };

  const updateBlockInSlot = (slotIndex: number, blockIndex: number, updates: Partial<ScheduledBlock>) => {
    const newSlots = [...timeSlots];
    newSlots[slotIndex].activityBlocks[blockIndex] = {
      ...newSlots[slotIndex].activityBlocks[blockIndex],
      ...updates
    };
    setTimeSlots(newSlots);
  };

  const addTaskToSlotBlock = (slotIndex: number, blockIndex: number) => {
    if (!availableTasks.length) return;
    const newSlots = [...timeSlots];
    const nonSleep = availableTasks.filter(t => t.task_type !== 'sleep');
    const defaultTask = nonSleep.length > 0 ? nonSleep[0] : availableTasks[0];
    newSlots[slotIndex].activityBlocks[blockIndex].scheduled_tasks.push({
      task_id: defaultTask.task_id,
      min_duration_seconds: 300,
      max_duration_seconds: 1800,
      number_of_tasks: 1
    });
    setTimeSlots(newSlots);
  };

  const removeTaskFromSlotBlock = (slotIndex: number, blockIndex: number, taskIndex: number) => {
    const newSlots = [...timeSlots];
    newSlots[slotIndex].activityBlocks[blockIndex].scheduled_tasks.splice(taskIndex, 1);
    setTimeSlots(newSlots);
  };

  const updateTaskInSlotBlock = (slotIndex: number, blockIndex: number, taskIndex: number, updates: Partial<ScheduledTask>) => {
    const newSlots = [...timeSlots];
    newSlots[slotIndex].activityBlocks[blockIndex].scheduled_tasks[taskIndex] = {
      ...newSlots[slotIndex].activityBlocks[blockIndex].scheduled_tasks[taskIndex],
      ...updates
    };
    setTimeSlots(newSlots);
  };

  const removeSleepSlot = (slotIndex: number) => {
    const newSlots = [...timeSlots];
    const removed = newSlots[slotIndex];
    
    if (slotIndex > 0 && newSlots[slotIndex - 1].type === 'activity') {
      newSlots[slotIndex - 1].endOffsetSeconds = removed.endOffsetSeconds;
      newSlots[slotIndex - 1].durationSeconds = newSlots[slotIndex - 1].endOffsetSeconds - newSlots[slotIndex - 1].startOffsetSeconds;
      newSlots[slotIndex - 1].localEnd = removed.localEnd;
    }
    
    newSlots.splice(slotIndex, 1);
    
    // Merge consecutive activity slots
    const merged: typeof timeSlots = [];
    for (const slot of newSlots) {
      if (merged.length > 0 && merged[merged.length - 1].type === 'activity' && slot.type === 'activity') {
        merged[merged.length - 1].endOffsetSeconds = slot.endOffsetSeconds;
        merged[merged.length - 1].durationSeconds = merged[merged.length - 1].endOffsetSeconds - merged[merged.length - 1].startOffsetSeconds;
        merged[merged.length - 1].localEnd = slot.localEnd;
        merged[merged.length - 1].activityBlocks.push(...slot.activityBlocks);
      } else {
        merged.push(slot);
      }
    }
    setTimeSlots(merged);
  };

  // Generate random labels for a persona
  const generateRandomLabels = (count: number = 5): string[] => {
    const shuffled = [...INTEREST_LABELS].sort(() => Math.random() - 0.5);
    return shuffled.slice(0, count);
  };

  // Initialize persona configs when count changes
  const initializePersonaConfigs = (count: number) => {
    const configs: PersonaConfig[] = [];
    for (let i = 0; i < count; i++) {
      configs.push({
        demographic: '',
        labels: generateRandomLabels(5),
        proxyId: proxyConfig.enabled ? proxyConfig.proxyId : null
      });
    }
    setPersonaConfigs(configs);
  };

  // Toggle region selection
  const toggleRegion = (region: RegionResponse) => {
    if (selectedRegions.find(r => r.regionId === region.regionId)) {
      setSelectedRegions(selectedRegions.filter(r => r.regionId !== region.regionId));
    } else {
      setSelectedRegions([...selectedRegions, region]);
    }
  };

  // Toggle device selection
  const toggleDevice = (device: DeviceInfoResponse) => {
    if (selectedDevices.find(d => d.deviceInfoId === device.deviceInfoId)) {
      setSelectedDevices(selectedDevices.filter(d => d.deviceInfoId !== device.deviceInfoId));
    } else {
      setSelectedDevices([...selectedDevices, device]);
    }
  };

  const handleCreateRegion = async () => {
    // Validate all required fields
    if (!newRegion.carrier_region || !newRegion.mcc_mnc || !newRegion.locale ||
        !newRegion.timezone_name || !newRegion.carrier_name || !newRegion.timezone_long_name) {
      setError('All region fields are required');
      return;
    }

    try {
      setLoading(true);
      setError(null);
      
      const created = await regionsApi.createRegionApiRegionsPost({
        createRegionRequest: {
          carrierRegion: newRegion.carrier_region.trim(),
          mccMnc: newRegion.mcc_mnc.trim(),
          locale: newRegion.locale.trim(),
          timezoneName: newRegion.timezone_name.trim(),
          carrierName: newRegion.carrier_name.trim(),
          timezoneLongName: newRegion.timezone_long_name.trim()
        }
      });
      setRegions([...regions, created]);
      setSelectedRegions([...selectedRegions, created]);
      setShowNewRegionForm(false);
      setNewRegion({
        carrier_region: '',
        mcc_mnc: '',
        locale: '',
        timezone_name: '',
        carrier_name: '',
        timezone_long_name: ''
      });
    } catch (err: any) {
      const errorMsg = err?.body?.detail || err?.message || 'Unknown error';
      setError(`Failed to create region: ${errorMsg}`);
      console.error('Region creation error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateDevice = async () => {
    // Validate all required fields
    if (!newDevice.device_type || !newDevice.device_brand || !newDevice.device_platform ||
        !newDevice.device_os_version || !newDevice.device_os_api || !newDevice.web_ua ||
        !newDevice.cpu_abi || !newDevice.resolution_width || !newDevice.resolution_height || !newDevice.dpi) {
      setError('All device fields are required');
      return;
    }

    try {
      setLoading(true);
      setError(null);
      
      const created = await devicesApi.createDeviceInfoApiDeviceInfosPost({
        createDeviceInfoRequest: {
          deviceType: newDevice.device_type.trim(),
          deviceBrand: newDevice.device_brand.trim(),
          devicePlatform: newDevice.device_platform.trim(),
          deviceOsVersion: Number(newDevice.device_os_version),
          deviceOsApi: Number(newDevice.device_os_api),
          webUa: newDevice.web_ua.trim(),
          cpuAbi: newDevice.cpu_abi.trim(),
          resolutionWidth: Number(newDevice.resolution_width),
          resolutionHeight: Number(newDevice.resolution_height),
          dpi: Number(newDevice.dpi)
        }
      });
      setDevices([...devices, created]);
      setSelectedDevices([...selectedDevices, created]);
      setShowNewDeviceForm(false);
      setNewDevice({
        device_brand: '',
        device_platform: '',
        device_type: '',
        device_os_api: 34,
        device_os_version: 13,
        resolution_width: 1080,
        resolution_height: 2400,
        dpi: 440,
        cpu_abi: '',
        web_ua: ''
      });
    } catch (err: any) {
      const errorMsg = err?.body?.detail || err?.message || 'Unknown error';
      setError(`Failed to create device: ${errorMsg}`);
      console.error('Device creation error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleDeploy = async () => {
    if (selectedRegions.length === 0 || selectedDevices.length === 0 || !selectedClassifier || !groupName) {
      setError('Please complete all required fields');
      return;
    }

    try {
      setLoading(true);
      setError(null);

      // Handle proxy creation if needed
      let finalProxyId = proxyConfig.proxyId;
      if (proxyConfig.enabled && !proxyConfig.proxyId) {
        if (proxyConfig.createMode === 'brightdata' && proxyConfig.brightDataProxyName && proxyConfig.brightDataCountry) {
          // Confirm cost before creating Bright Data ISP proxy
          const totalMonthlyCost = proxyConfig.brightDataIpCount * 2;
          const confirmed = window.confirm(
            `This will create ${proxyConfig.brightDataIpCount} ISP proxy IP(s) at $2/IP/month.\n\n` +
            `Total monthly cost: $${totalMonthlyCost}\n\n` +
            `Do you want to proceed?`
          );
          if (!confirmed) {
            setLoading(false);
            return;
          }

          // Create Bright Data ISP proxy
          const newProxy = await proxiesApi.createBrightDataIspProxyApiProxiesBrightDataIspCreatePost({
            createBrightDataISPProxyRequest: {
              proxyName: proxyConfig.brightDataProxyName,
              countryCode: proxyConfig.brightDataCountry,
              ipCount: proxyConfig.brightDataIpCount
            }
          });
          finalProxyId = newProxy.proxyId;
        } else if (proxyConfig.createMode === 'url' && proxyConfig.newProxyUrl) {
          // Create new proxy from URL
          const newProxy = await proxiesApi.createProxyFromUrlApiProxiesPost({
            createProxyFromUrlRequest: { proxyUrl: proxyConfig.newProxyUrl }
          });
          finalProxyId = newProxy.proxyId;
        }
      }

      // Create all personas in a single bulk request
      const personaRequests = Array.from({ length: personasToCreate }, (_, i) => {
        const randomRegion = selectedRegions[Math.floor(Math.random() * selectedRegions.length)];
        const randomDevice = selectedDevices[Math.floor(Math.random() * selectedDevices.length)];
        const config = personaConfigs[i];
        return {
          personaType: PersonaType.Bot,
          labels: config.labels,
          demographic: config.demographic || null,
          regionId: randomRegion.regionId,
          deviceInfoId: randomDevice.deviceInfoId!,
          proxyId: proxyConfig.enabled ? (config.proxyId || finalProxyId) : null,
        };
      });

      const bulkResult = await personasApi.bulkCreatePersonasApiPersonasBulkPost({
        bulkCreatePersonasRequest: {
          personas: personaRequests.map(p => ({
            personaType: p.personaType,
            labels: p.labels,
            demographic: p.demographic,
            regionId: p.regionId,
            deviceInfoId: p.deviceInfoId,
            proxyId: p.proxyId,
          })),
        },
      });
      const createdUserIds = bulkResult.userIds;

      if (!createdUserIds || createdUserIds.length === 0) {
        throw new Error('Bulk persona creation returned no user IDs');
      }
      if (createdUserIds.length !== personasToCreate) {
        throw new Error(
          `Expected ${personasToCreate} personas but got ${createdUserIds.length}`
        );
      }

      // Create persona group with narratives
      const createdGroup = await personaGroupsApi.createPersonaGroupApiPersonaGroupsPost({
        createPersonaGroupRequest: {
          name: groupName,
          classifierId: selectedClassifier.classifierId,
          userIds: createdUserIds,
          narrativeMappingIds: selectedNarratives.length > 0 ? selectedNarratives : undefined
        } as any
      });

      // Handle experiment assignment
      if (experimentMode === 'existing' && selectedExperiment) {
        // Add group to existing experiment
        await fetch(`/api/experiments/${selectedExperiment.experimentId}/groups`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ group_ids: [createdGroup.groupId] })
        });
      } else if (experimentMode === 'new') {
        // Create new experiment with this group
        await experimentsApi.createExperimentApiExperimentsPost({
          experimentCreate: {
            name: experimentName || `${groupName} Experiment`,
            durationSeconds: (totalDays || 3) * 24 * 60 * 60,
            biasTests: [],
            useSleepSchedule: useSleepSchedule,
            groupIds: [createdGroup.groupId]
          } as any
        });
      }

      // Schedule tasks for the group using the advanced scheduling system (if tasks configured)
      let finalBlocks: ScheduledBlock[] = [];
      let finalDuration = totalDuration;
      
      if (useSleepSchedule && timeSlots.length > 0) {
        // Convert timeSlots to blocks
        for (const slot of timeSlots) {
          if (slot.type === 'sleep' && slot.sleepTaskId) {
            finalBlocks.push({
              scheduled_tasks: [{
                task_id: slot.sleepTaskId,
                min_duration_seconds: slot.durationSeconds,
                max_duration_seconds: slot.durationSeconds,
                number_of_tasks: 1
              }],
              block_duration_seconds: slot.durationSeconds,
              tasks_order_type: 'sequential'
            });
          } else if (slot.type === 'activity') {
            for (const block of slot.activityBlocks) {
              finalBlocks.push(block);
            }
          }
        }
        finalDuration = totalDays * 24 * 60 * 60;
      } else {
        finalBlocks = scheduleBlocks;
      }
      
      const hasTasks = finalBlocks.some(block => block.scheduled_tasks.length > 0);
      if (hasTasks) {
        const scheduleData: ScheduleGroup = {
          scheduled_blocks: finalBlocks,
          total_duration_seconds: finalDuration,
          blocks_order_type: blocksOrderType,
          variance_percentage: variance,
          use_queue: useQueue,
        };

        const endpoint = useQueue ? '/api/tasks/populate_queue' : '/api/tasks/schedule_group';
        await fetch(endpoint, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            group_id: createdGroup.groupId,
            schedule: scheduleData
          })
        });
      }

      if (useQueue) {
        alert(`Bot group "${groupName}" created and tasks scheduled to queue for ${personasToCreate} personas!`);
      } else {
        // Start the bot group container (polling mode)
        try {
          await botsApi.reloadGroupsCacheApiBotsReloadGroupsCachePost();
          await botsApi.startBotApiBotsGroupIdStartPost({
            groupId: createdGroup.groupId,
            startBotRequest: { usersCount: personasToCreate }
          });
          alert(`Bot group "${groupName}" created and started successfully with ${personasToCreate} personas!`);
        } catch (startErr) {
          console.error('Failed to start bot group:', startErr);
          alert(`Bot group "${groupName}" created successfully, but failed to start automatically. You can start it manually from Bot Status page.`);
        }
      }

      // Save current selections to localStorage for next time
      try {
        localStorage.setItem(WIZARD_STORAGE_KEY, JSON.stringify({
          selectedRegionIds: selectedRegions.map(r => r.regionId),
          selectedDeviceIds: selectedDevices.map(d => d.deviceInfoId),
          selectedClassifierId: selectedClassifier?.classifierId ?? null,
          selectedNarrativeIds: selectedNarratives,
          personasToCreate,
          proxyConfig,
          experimentMode,
          selectedExperimentId: selectedExperiment?.experimentId ?? null,
          useSleepSchedule,
          totalDays,
          sleepStartHour,
          sleepEndHour,
          sleepVariance,
          researchMode,
          dailyTikTokHours,
          blocksOrderType,
          variance,
          useQueue,
          totalDuration,
          defaultBlockDuration,
        }));
      } catch { /* localStorage might be full */ }

      // Reset wizard for next use (but localStorage keeps preferences)
      setCurrentStep(1);
      setGroupName('');
      setPersonaConfigs([]);
      setExperimentName('');
      
    } catch (err) {
      console.error('Deployment error:', err);
      setError('Failed to deploy bot group. Check console for details.');
    } finally {
      setLoading(false);
    }
  };

  const canProceed = () => {
    switch (currentStep) {
      case 1: return selectedRegions.length > 0;
      case 2: return selectedDevices.length > 0;
      case 3: return personasToCreate > 0 && personaConfigs.length === personasToCreate;
      case 4: return selectedClassifier !== null;
      case 5: return true; // Allow skipping task configuration
      case 6: return groupName.length > 0 && (experimentMode === 'new' || selectedExperiment !== null);
      default: return true;
    }
  };

  const renderStepContent = () => {
    switch (currentStep) {
      case 1:
        return (
          <div className="step-content">
            <h2>Select Regions</h2>
            <p className="text-muted">Choose one or more geographic regions - bots will be randomly assigned</p>
            <div className="badge badge-primary" style={{ marginTop: '1rem' }}>
              {selectedRegions.length} region{selectedRegions.length !== 1 ? 's' : ''} selected
            </div>
            
            <div className="grid grid-cols-3" style={{ marginTop: '2rem' }}>
              {regions.map(region => (
                <div
                  key={region.regionId}
                  className={`card selectable ${selectedRegions.find(r => r.regionId === region.regionId) ? 'selected' : ''}`}
                  onClick={() => toggleRegion(region)}
                >
                  <h3>{countryCodeToFlag(region.carrierRegion)} {region.carrierRegion.toUpperCase()}</h3>
                  <p className="text-muted">{region.carrierName}</p>
                  <div className="text-secondary" style={{ fontSize: '0.75rem', marginTop: '0.5rem' }}>
                    {region.locale} • {region.timezoneName}
                  </div>
                  {selectedRegions.find(r => r.regionId === region.regionId) && (
                    <div style={{ 
                      position: 'absolute', 
                      top: '0.5rem', 
                      right: '0.5rem', 
                      background: 'var(--accent-green)', 
                      borderRadius: '50%',
                      width: '24px',
                      height: '24px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: 'white',
                      fontWeight: 'bold'
                    }}>
                      ✓
                    </div>
                  )}
                </div>
              ))}
            </div>

            <button
              className="btn btn-secondary"
              style={{ marginTop: '2rem' }}
              onClick={() => setShowNewRegionForm(!showNewRegionForm)}
            >
              {showNewRegionForm ? '− Cancel' : '+ Create New Region'}
            </button>

            {showNewRegionForm && (
              <div className="card" style={{ marginTop: '1rem' }}>
                <h3>New Region</h3>
                <div className="grid grid-cols-2">
                  <div className="form-group">
                    <label className="form-label">Carrier Region *</label>
                    <input
                      type="text"
                      value={newRegion.carrier_region}
                      onChange={e => setNewRegion({ ...newRegion, carrier_region: e.target.value })}
                      placeholder="e.g., us"
                      required
                    />
                    <p className="form-help">2-letter region code</p>
                  </div>
                  <div className="form-group">
                    <label className="form-label">Carrier Name *</label>
                    <input
                      type="text"
                      value={newRegion.carrier_name}
                      onChange={e => setNewRegion({ ...newRegion, carrier_name: e.target.value })}
                      placeholder="e.g., T-Mobile"
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label className="form-label">MCC-MNC *</label>
                    <input
                      type="text"
                      value={newRegion.mcc_mnc}
                      onChange={e => setNewRegion({ ...newRegion, mcc_mnc: e.target.value })}
                      placeholder="e.g., 310-260"
                      required
                    />
                    <p className="form-help">Mobile Country Code - Mobile Network Code</p>
                  </div>
                  <div className="form-group">
                    <label className="form-label">Locale *</label>
                    <input
                      type="text"
                      value={newRegion.locale}
                      onChange={e => setNewRegion({ ...newRegion, locale: e.target.value })}
                      placeholder="e.g., en_US"
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Timezone Name *</label>
                    <input
                      type="text"
                      value={newRegion.timezone_name}
                      onChange={e => setNewRegion({ ...newRegion, timezone_name: e.target.value })}
                      placeholder="e.g., America/Los_Angeles"
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Timezone Long Name *</label>
                    <input
                      type="text"
                      value={newRegion.timezone_long_name}
                      onChange={e => setNewRegion({ ...newRegion, timezone_long_name: e.target.value })}
                      placeholder="e.g., Pacific Standard Time"
                      required
                    />
                  </div>
                </div>
                <button
                  className="btn btn-primary"
                  onClick={handleCreateRegion}
                  disabled={loading || !newRegion.carrier_region || !newRegion.carrier_name || !newRegion.mcc_mnc || !newRegion.locale || !newRegion.timezone_name || !newRegion.timezone_long_name}
                >
                  Create Region
                </button>
              </div>
            )}
          </div>
        );

      case 2:
        return (
          <div className="step-content">
            <h2>Select Devices</h2>
            <p className="text-muted">Choose one or more device configurations - bots will be randomly assigned</p>
            <div className="badge badge-primary" style={{ marginTop: '1rem' }}>
              {selectedDevices.length} device{selectedDevices.length !== 1 ? 's' : ''} selected
            </div>
            
            <div className="grid grid-cols-2" style={{ marginTop: '2rem' }}>
              {devices.map(device => (
                <div
                  key={device.deviceInfoId}
                  className={`card selectable ${selectedDevices.find(d => d.deviceInfoId === device.deviceInfoId) ? 'selected' : ''}`}
                  onClick={() => toggleDevice(device)}
                >
                  <h3>{device.deviceType}</h3>
                  <p className="text-muted">{device.deviceBrand} • {device.devicePlatform}</p>
                  <div className="text-secondary" style={{ fontSize: '0.75rem', marginTop: '0.5rem' }}>
                    {device.resolutionWidth}x{device.resolutionHeight} • {device.dpi} DPI
                  </div>
                  {selectedDevices.find(d => d.deviceInfoId === device.deviceInfoId) && (
                    <div style={{ 
                      position: 'absolute', 
                      top: '0.5rem', 
                      right: '0.5rem', 
                      background: 'var(--accent-green)', 
                      borderRadius: '50%',
                      width: '24px',
                      height: '24px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: 'white',
                      fontWeight: 'bold'
                    }}>
                      ✓
                    </div>
                  )}
                </div>
              ))}
            </div>

            <button
              className="btn btn-secondary"
              style={{ marginTop: '2rem' }}
              onClick={() => setShowNewDeviceForm(!showNewDeviceForm)}
            >
              {showNewDeviceForm ? '− Cancel' : '+ Create New Device'}
            </button>

            {showNewDeviceForm && (
              <div className="card" style={{ marginTop: '1rem' }}>
                <h3>New Device</h3>
                <div className="grid grid-cols-2">
                  <div className="form-group">
                    <label className="form-label">Device Brand *</label>
                    <input
                      type="text"
                      value={newDevice.device_brand}
                      onChange={e => setNewDevice({ ...newDevice, device_brand: e.target.value })}
                      placeholder="e.g., Samsung"
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Device Type *</label>
                    <input
                      type="text"
                      value={newDevice.device_type}
                      onChange={e => setNewDevice({ ...newDevice, device_type: e.target.value })}
                      placeholder="e.g., SM-G998B"
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Platform *</label>
                    <input
                      type="text"
                      value={newDevice.device_platform}
                      onChange={e => setNewDevice({ ...newDevice, device_platform: e.target.value })}
                      placeholder="e.g., Android"
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label className="form-label">OS Version *</label>
                    <input
                      type="number"
                      value={newDevice.device_os_version}
                      onChange={e => setNewDevice({ ...newDevice, device_os_version: parseInt(e.target.value) || 13 })}
                      placeholder="e.g., 13"
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label className="form-label">OS API Level *</label>
                    <input
                      type="number"
                      value={newDevice.device_os_api}
                      onChange={e => setNewDevice({ ...newDevice, device_os_api: parseInt(e.target.value) || 34 })}
                      placeholder="e.g., 34"
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label className="form-label">CPU ABI *</label>
                    <input
                      type="text"
                      value={newDevice.cpu_abi}
                      onChange={e => setNewDevice({ ...newDevice, cpu_abi: e.target.value })}
                      placeholder="e.g., arm64-v8a"
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Resolution Width *</label>
                    <input
                      type="number"
                      value={newDevice.resolution_width}
                      onChange={e => setNewDevice({ ...newDevice, resolution_width: parseInt(e.target.value) || 1080 })}
                      placeholder="e.g., 1080"
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Resolution Height *</label>
                    <input
                      type="number"
                      value={newDevice.resolution_height}
                      onChange={e => setNewDevice({ ...newDevice, resolution_height: parseInt(e.target.value) || 2400 })}
                      placeholder="e.g., 2400"
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label className="form-label">DPI *</label>
                    <input
                      type="number"
                      value={newDevice.dpi}
                      onChange={e => setNewDevice({ ...newDevice, dpi: parseInt(e.target.value) || 440 })}
                      placeholder="e.g., 440"
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Web User Agent *</label>
                    <input
                      type="text"
                      value={newDevice.web_ua}
                      onChange={e => setNewDevice({ ...newDevice, web_ua: e.target.value })}
                      placeholder="Mozilla/5.0..."
                      required
                    />
                  </div>
                </div>
                <button
                  className="btn btn-primary"
                  onClick={handleCreateDevice}
                  disabled={loading || !newDevice.device_brand || !newDevice.device_type || !newDevice.device_platform || !newDevice.device_os_version || !newDevice.cpu_abi || !newDevice.web_ua}
                >
                  Create Device
                </button>
              </div>
            )}
          </div>
        );

      case 3:
        return (
          <div className="step-content">
            <h2>Configure Personas</h2>
            <p className="text-muted">Set up individual bot personas - each with unique interests</p>
            
            <div className="card" style={{ marginTop: '2rem' }}>
              <div className="form-group">
                <label className="form-label">Number of Personas</label>
                <div style={{ display: 'flex', gap: '1rem', alignItems: 'center' }}>
                  <input
                    type="number"
                    value={personasToCreate}
                    onChange={e => {
                      const count = parseInt(e.target.value) || 1;
                      setPersonasToCreate(count);
                    }}
                    min="1"
                    max="100"
                    style={{ flex: '0 0 150px' }}
                  />
                  <button
                    className="btn btn-primary"
                    onClick={() => initializePersonaConfigs(personasToCreate)}
                  >
                    Generate Personas
                  </button>
                </div>
                <p className="form-help">Set number and click Generate to create personas with random interests</p>
              </div>

              <div className="divider"></div>

              <h3 style={{ marginBottom: '1rem' }}>Proxy Configuration (Optional)</h3>
              
              <div className="form-group">
                <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={proxyConfig.enabled}
                    onChange={e => setProxyConfig({ ...proxyConfig, enabled: e.target.checked })}
                    style={{ width: 'auto' }}
                  />
                  <span className="form-label" style={{ marginBottom: 0 }}>Use Proxy</span>
                </label>
              </div>

              {proxyConfig.enabled && (
                <>
                  <div className="form-group">
                    <label className="form-label">Select Existing Proxy</label>
                    <select
                      value={proxyConfig.proxyId || ''}
                      onChange={e => setProxyConfig({ ...proxyConfig, proxyId: e.target.value ? Number(e.target.value) : null, newProxyUrl: '' })}
                    >
                      <option value="">-- Create New Proxy --</option>
                      {proxies.map(proxy => (
                        <option key={proxy.proxyId} value={proxy.proxyId}>
                          {getProxyDisplayName(proxy)} (ID: {proxy.proxyId})
                        </option>
                      ))}
                    </select>
                    <p className="form-help">Select an existing proxy or create a new one below</p>
                  </div>

                  {!proxyConfig.proxyId && (
                    <>
                      <div className="form-group">
                        <label className="form-label">Creation Method</label>
                        <div style={{ display: 'flex', gap: '1rem', marginBottom: '1rem' }}>
                          <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
                            <input
                              type="radio"
                              name="proxyCreateMode"
                              checked={proxyConfig.createMode === 'url'}
                              onChange={() => setProxyConfig({ ...proxyConfig, createMode: 'url' })}
                            />
                            Manual URL
                          </label>
                          <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
                            <input
                              type="radio"
                              name="proxyCreateMode"
                              checked={proxyConfig.createMode === 'brightdata'}
                              onChange={async () => {
                                // Check if all selected regions have the same carrier region
                                const carrierRegions = selectedRegions.map(r => r.carrierRegion.toLowerCase());
                                const uniqueRegions = Array.from(new Set(carrierRegions));
                                const sameRegion = uniqueRegions.length === 1 ? uniqueRegions[0] : null;
                                
                                // Generate default proxy name if all regions match
                                const defaultProxyName = sameRegion 
                                  ? `${sameRegion}_proxy_${Math.floor(Date.now() / 1000)}`
                                  : '';
                                
                                // Load countries if not loaded
                                let countries = ispCountries;
                                if (Object.keys(ispCountries).length === 0) {
                                  setLoadingCountries(true);
                                  try {
                                    countries = await proxiesApi.getBrightDataIspCountriesApiProxiesIspCountriesGet({});
                                    setIspCountries(countries);
                                  } catch (err) {
                                    console.error('Failed to load ISP countries:', err);
                                  } finally {
                                    setLoadingCountries(false);
                                  }
                                }
                                
                                // Auto-select country if carrier region matches a country code
                                let autoCountry = '';
                                if (sameRegion && Object.values(countries).includes(sameRegion)) {
                                  autoCountry = sameRegion;
                                }
                                
                                setProxyConfig({ 
                                  ...proxyConfig, 
                                  createMode: 'brightdata',
                                  brightDataProxyName: defaultProxyName,
                                  brightDataCountry: autoCountry
                                });
                              }}
                            />
                            Bright Data ISP
                          </label>
                        </div>
                      </div>

                      {proxyConfig.createMode === 'url' ? (
                        <div className="form-group">
                          <label className="form-label">New Proxy URL</label>
                          <input
                            type="text"
                            value={proxyConfig.newProxyUrl}
                            onChange={e => setProxyConfig({ ...proxyConfig, newProxyUrl: e.target.value })}
                            placeholder="http://proxy.example.com:8080"
                          />
                          <p className="form-help">Format: http://[user:pass@]host:port</p>
                        </div>
                      ) : (
                        <>
                          <div className="form-group">
                            <label className="form-label">Proxy Name *</label>
                            <input
                              type="text"
                              value={proxyConfig.brightDataProxyName}
                              onChange={e => setProxyConfig({ ...proxyConfig, brightDataProxyName: e.target.value })}
                              placeholder="us-proxy-1765792736"
                            />
                            <p className="form-help">A unique name for your Bright Data ISP proxy zone</p>
                          </div>
                          <div className="form-group">
                            <label className="form-label">Country *</label>
                            {loadingCountries ? (
                              <p>Loading countries...</p>
                            ) : (
                              <div style={{ 
                                border: '1px solid var(--border-default)', 
                                borderRadius: '8px', 
                                overflow: 'hidden',
                                background: 'var(--bg-secondary)'
                              }}>
                                {/* Collapsed view / Toggle button */}
                                <div 
                                  onClick={() => setCountryPickerOpen(!countryPickerOpen)}
                                  style={{ 
                                    padding: '0.75rem 1rem', 
                                    cursor: 'pointer',
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'center',
                                    background: 'var(--bg-primary)',
                                    borderBottom: countryPickerOpen ? '1px solid var(--border-default)' : 'none'
                                  }}
                                >
                                  {proxyConfig.brightDataCountry ? (
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                      <span>{countryCodeToFlag(proxyConfig.brightDataCountry)}</span>
                                      <strong>{Object.entries(ispCountries).find(([_, code]) => code === proxyConfig.brightDataCountry)?.[0]}</strong>
                                      <span style={{ opacity: 0.6 }}>({proxyConfig.brightDataCountry})</span>
                                    </div>
                                  ) : (
                                    <span style={{ opacity: 0.6 }}>Select a country...</span>
                                  )}
                                  <span style={{ 
                                    transform: countryPickerOpen ? 'rotate(180deg)' : 'rotate(0deg)',
                                    transition: 'transform 0.2s ease'
                                  }}>▼</span>
                                </div>

                                {/* Expanded view */}
                                {countryPickerOpen && (
                                  <>
                                    <div style={{ padding: '0.75rem', borderBottom: '1px solid var(--border-default)' }}>
                                      <input
                                        type="text"
                                        value={countrySearch}
                                        onChange={e => setCountrySearch(e.target.value)}
                                        placeholder="🔍 Search countries..."
                                        style={{ 
                                          width: '100%', 
                                          margin: 0,
                                          background: 'var(--bg-primary)',
                                          border: '1px solid var(--border-default)',
                                          borderRadius: '6px',
                                          padding: '0.5rem 0.75rem'
                                        }}
                                        autoFocus
                                      />
                                    </div>
                                    <div style={{ maxHeight: '200px', overflowY: 'auto' }}>
                                      {Object.entries(ispCountries)
                                        .filter(([name, code]) => 
                                          name.toLowerCase().includes(countrySearch.toLowerCase()) ||
                                          (code && code.toLowerCase().includes(countrySearch.toLowerCase()))
                                        )
                                        .sort(([nameA], [nameB]) => nameA.localeCompare(nameB))
                                        .map(([name, code]) => (
                                          <div
                                            key={code}
                                            onClick={() => {
                                              setProxyConfig({ ...proxyConfig, brightDataCountry: code || '' });
                                              setCountryPickerOpen(false);
                                              setCountrySearch('');
                                            }}
                                            style={{
                                              padding: '0.6rem 1rem',
                                              cursor: 'pointer',
                                              background: proxyConfig.brightDataCountry === code ? 'var(--accent-primary)' : 'transparent',
                                              color: proxyConfig.brightDataCountry === code ? 'white' : 'inherit',
                                              borderBottom: '1px solid var(--border-default)',
                                              display: 'flex',
                                              justifyContent: 'space-between',
                                              alignItems: 'center',
                                              transition: 'background 0.15s ease'
                                            }}
                                            onMouseEnter={e => {
                                              if (proxyConfig.brightDataCountry !== code) {
                                                e.currentTarget.style.background = 'var(--bg-hover)';
                                              }
                                            }}
                                            onMouseLeave={e => {
                                              if (proxyConfig.brightDataCountry !== code) {
                                                e.currentTarget.style.background = 'transparent';
                                              }
                                            }}
                                          >
                                            <span>{countryCodeToFlag(code)} {name}</span>
                                            <span style={{ 
                                              opacity: 0.7, 
                                              fontSize: '0.85rem',
                                              fontFamily: 'monospace'
                                            }}>{code}</span>
                                          </div>
                                        ))}
                                    </div>
                                  </>
                                )}
                              </div>
                            )}
                          </div>
                          <div className="form-group">
                            <label className="form-label">Number of IPs *</label>
                            <input
                              type="number"
                              min={1}
                              max={100}
                              value={proxyConfig.brightDataIpCount}
                              onChange={e => setProxyConfig({ ...proxyConfig, brightDataIpCount: parseInt(e.target.value) || 1 })}
                            />
                            <p className="form-help">Number of static IPs to allocate (1-100)</p>
                          </div>
                        </>
                      )}
                    </>
                  )}
                </>
              )}
            </div>

            {personaConfigs.length > 0 && (
              <div style={{ marginTop: '2rem' }}>
                <h3 style={{ marginBottom: '1rem' }}>Persona Configurations ({personaConfigs.length})</h3>
                <div style={{ 
                  display: 'grid', 
                  gridTemplateColumns: 'repeat(3, 1fr)', 
                  gap: '12px',
                  maxHeight: '500px',
                  overflowY: 'auto',
                  padding: '4px'
                }}>
                  {personaConfigs.map((config, idx) => (
                    <div key={idx} style={{
                      background: '#1a1a1a',
                      border: '1px solid rgba(99, 102, 241, 0.3)',
                      borderRadius: '8px',
                      padding: '12px',
                      fontSize: '12px'
                    }}>
                      <div style={{ 
                        display: 'flex', 
                        justifyContent: 'space-between', 
                        alignItems: 'center',
                        marginBottom: '8px',
                        borderBottom: '1px solid rgba(255,255,255,0.1)',
                        paddingBottom: '8px'
                      }}>
                        <span style={{ color: '#a5b4fc', fontWeight: '600' }}>🤖 Persona #{idx + 1}</span>
                        <button
                          onClick={() => {
                            const newConfigs = [...personaConfigs];
                            newConfigs[idx].labels = generateRandomLabels(5);
                            setPersonaConfigs(newConfigs);
                          }}
                          style={{
                            background: 'rgba(99, 102, 241, 0.2)',
                            border: '1px solid rgba(99, 102, 241, 0.4)',
                            color: '#a5b4fc',
                            borderRadius: '4px',
                            padding: '2px 6px',
                            cursor: 'pointer',
                            fontSize: '10px'
                          }}
                          title="Regenerate labels"
                        >
                          🎲
                        </button>
                      </div>
                      
                      <div style={{ marginBottom: '8px' }}>
                        <label style={{ fontSize: '10px', color: 'rgba(255,255,255,0.5)', display: 'block', marginBottom: '4px' }}>Demo</label>
                        <input
                          type="text"
                          value={config.demographic}
                          onChange={e => {
                            const newConfigs = [...personaConfigs];
                            newConfigs[idx].demographic = e.target.value;
                            setPersonaConfigs(newConfigs);
                          }}
                          placeholder="18-24, Male..."
                          style={{
                            width: '100%',
                            padding: '4px 8px',
                            background: 'rgba(255,255,255,0.05)',
                            border: '1px solid rgba(255,255,255,0.2)',
                            borderRadius: '4px',
                            color: 'white',
                            fontSize: '11px'
                          }}
                        />
                      </div>

                      <div>
                        <label style={{ fontSize: '10px', color: 'rgba(255,255,255,0.5)', display: 'block', marginBottom: '4px' }}>Labels</label>
                        <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap', marginBottom: '4px' }}>
                          {config.labels.slice(0, 3).map((label, labelIdx) => (
                            <span key={labelIdx} style={{
                              background: 'rgba(99, 102, 241, 0.2)',
                              padding: '2px 6px',
                              borderRadius: '4px',
                              fontSize: '9px',
                              color: '#a5b4fc',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '4px'
                            }}>
                              {label}
                              <button
                                onClick={() => {
                                  const newConfigs = [...personaConfigs];
                                  newConfigs[idx].labels = config.labels.filter((_, i) => i !== labelIdx);
                                  setPersonaConfigs(newConfigs);
                                }}
                                style={{ 
                                  background: 'none', 
                                  border: 'none', 
                                  color: 'rgba(255,255,255,0.5)', 
                                  cursor: 'pointer',
                                  padding: 0,
                                  fontSize: '10px'
                                }}
                              >
                                ×
                              </button>
                            </span>
                          ))}
                          {config.labels.length > 3 && (
                            <span style={{ fontSize: '9px', color: 'rgba(255,255,255,0.5)' }}>+{config.labels.length - 3}</span>
                          )}
                        </div>
                        <input
                          type="text"
                          placeholder="+ Add label (Enter)"
                          onKeyDown={e => {
                            if (e.key === 'Enter' && e.currentTarget.value) {
                              const newConfigs = [...personaConfigs];
                              newConfigs[idx].labels.push(e.currentTarget.value);
                              setPersonaConfigs(newConfigs);
                              e.currentTarget.value = '';
                            }
                          }}
                          style={{
                            width: '100%',
                            padding: '4px 8px',
                            background: 'rgba(255,255,255,0.05)',
                            border: '1px solid rgba(255,255,255,0.2)',
                            borderRadius: '4px',
                            color: 'white',
                            fontSize: '10px'
                          }}
                        />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        );

      case 4:
        return (
          <div className="step-content">
            <h2>Select Classifier & Narrative Mappings</h2>
            <p className="text-muted">Choose a content classifier and narrative mappings for this group</p>
            
            <h3 style={{ marginTop: '2rem', marginBottom: '1rem' }}>Classifier</h3>
            <div className="grid grid-cols-2" style={{ marginTop: '1rem' }}>
              {classifiers.map(classifier => {
                const isNarrativeType = classifier.classifierType === 'narrative_mappings';
                const stanceMap = isNarrativeType ? classifier.classifierParams?.narrative_to_wanted_stance : null;
                return (
                  <div
                    key={classifier.classifierId}
                    className={`card selectable ${selectedClassifier?.classifierId === classifier.classifierId ? 'selected' : ''}`}
                    onClick={() => setSelectedClassifier(classifier)}
                  >
                    <h3>{classifier.classifierType}</h3>
                    <p className="text-muted">ID: {classifier.classifierId}</p>
                    {isNarrativeType && stanceMap ? (
                      <div style={{ marginTop: '0.5rem', display: 'flex', flexDirection: 'column', gap: '0.375rem' }}>
                        {Object.entries(stanceMap).map(([nId, stance]) => {
                          const narrative = narratives.find(n => n.narrativeId === parseInt(nId));
                          return (
                            <div key={nId} style={{
                              display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                              padding: '0.375rem 0.5rem', background: 'rgba(0,0,0,0.1)', borderRadius: '4px', fontSize: '0.8rem'
                            }}>
                              <span style={{ fontWeight: 500 }}>{narrative?.name || `Narrative ${nId}`}</span>
                              <span className="badge badge-secondary" style={{ fontSize: '0.7rem' }}>{String(stance)}</span>
                            </div>
                          );
                        })}
                        {Object.keys(stanceMap).length === 0 && (
                          <p className="text-muted" style={{ fontSize: '0.75rem' }}>No narratives configured</p>
                        )}
                      </div>
                    ) : (
                      <pre style={{ 
                        fontSize: '0.75rem', 
                        marginTop: '0.5rem', 
                        background: 'rgba(0,0,0,0.1)', 
                        padding: '0.5rem', 
                        borderRadius: '4px',
                        overflow: 'auto',
                        maxHeight: '150px'
                      }}>
                        {JSON.stringify(classifier.classifierParams, null, 2)}
                      </pre>
                    )}
                  </div>
                );
              })}
            </div>

            <h3 style={{ marginTop: '2rem', marginBottom: '1rem' }}>Narrative Mappings (Optional)</h3>
            <p className="text-muted" style={{ marginBottom: '1rem' }}>Select which narratives should classify content for this group</p>
            <div className="badge badge-primary" style={{ marginBottom: '1rem' }}>
              {selectedNarratives.length} narrative{selectedNarratives.length !== 1 ? 's' : ''} selected
            </div>
            
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
              {narratives.map(narrative => (
                <div
                  key={narrative.narrativeId}
                  className={`card selectable ${selectedNarratives.includes(narrative.narrativeId) ? 'selected' : ''}`}
                  onClick={() => {
                    if (selectedNarratives.includes(narrative.narrativeId)) {
                      setSelectedNarratives(selectedNarratives.filter(id => id !== narrative.narrativeId));
                    } else {
                      setSelectedNarratives([...selectedNarratives, narrative.narrativeId]);
                    }
                  }}
                  style={{ cursor: 'pointer', padding: '1rem' }}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <div style={{ flex: 1 }}>
                      <h4 style={{ margin: 0 }}>{narrative.name}</h4>
                      {narrative.description && (
                        <p className="text-muted" style={{ fontSize: '0.875rem', marginTop: '0.25rem' }}>
                          {narrative.description}
                        </p>
                      )}
                      <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.5rem' }}>
                        <span className="badge badge-secondary">ID: {narrative.narrativeId}</span>
                        <span className="badge badge-secondary">v{narrative.version}</span>
                        <span className="badge badge-secondary">{narrative.modelName}</span>
                      </div>
                    </div>
                    {selectedNarratives.includes(narrative.narrativeId) && (
                      <div style={{ 
                        background: 'var(--accent-green)', 
                        borderRadius: '50%',
                        width: '24px',
                        height: '24px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: 'white',
                        fontWeight: 'bold',
                        flexShrink: 0
                      }}>
                        ✓
                      </div>
                    )}
                  </div>
                </div>
              ))}
              {narratives.length === 0 && (
                <div className="card" style={{ padding: '2rem', textAlign: 'center' }}>
                  <p className="text-muted">No narratives available. Create narratives in the Configuration page.</p>
                </div>
              )}
            </div>
          </div>
        );

      case 5:
        return (
          <div className="step-content">
            <h2>Configure Tasks Schedule</h2>
            <p className="text-muted">Create block-based schedules with flexible task ordering</p>
            <p className="text-muted" style={{ fontSize: '0.875rem', marginBottom: '1rem' }}>
              ℹ️ Task scheduling is optional - you can skip this step and add tasks later from Command & Control
            </p>

            {availableTasks.length === 0 ? (
              <div className="card" style={{ padding: '2rem', textAlign: 'center' }}>
                <p>Loading available tasks...</p>
                <p className="text-muted" style={{ fontSize: '0.875rem', marginTop: '0.5rem' }}>
                  If no tasks appear, you may need to create tasks first from the Command & Control page
                </p>
              </div>
            ) : (
              <>
                {/* Schedule Mode Toggle */}
                <div className="card" style={{ marginBottom: '1.5rem', padding: '1rem' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '1rem' }}>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
                      <input 
                        type="checkbox" 
                        checked={useSleepSchedule} 
                        onChange={(e) => setUseSleepSchedule(e.target.checked)}
                        style={{ width: '18px', height: '18px' }}
                      />
                      <span style={{ fontWeight: '500' }}>🌙 Use Automatic Sleep Schedule</span>
                    </label>
                    {useSleepSchedule && selectedRegions.length > 0 && (
                      <span className="text-muted" style={{ fontSize: '0.875rem' }}>
                        Based on {selectedRegions[0].carrierRegion} timezone ({selectedRegions[0].timezoneName})
                      </span>
                    )}
                  </div>
                  
                  {useSleepSchedule ? (
                    <>
                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: '1rem' }}>
                        <div className="form-group">
                          <label>Duration (days)</label>
                          <input type="number" value={totalDays} onChange={(e) => setTotalDays(Math.max(1, +e.target.value))} min="1" max="30" />
                        </div>
                        <div className="form-group">
                          <label>Research Mode</label>
                          <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', minHeight: '38px' }}>
                            <input type="checkbox" checked={researchMode} onChange={(e) => setResearchMode(e.target.checked)} />
                            <span style={{ fontSize: '0.875rem' }}>Random sleep + For You</span>
                          </label>
                        </div>
                        <div className="form-group">
                          <label>TikTok / Day (hours)</label>
                          <input
                            type="number"
                            value={dailyTikTokHours}
                            onChange={(e) => setDailyTikTokHours(Math.max(0, +e.target.value))}
                            min="0"
                            max="24"
                            step="0.25"
                            disabled={researchMode}
                          />
                        </div>
                        <div className="form-group">
                          <label>Sleep Start</label>
                          <select value={sleepStartHour} onChange={(e) => setSleepStartHour(+e.target.value)}>
                            {Array.from({length: 24}, (_, i) => <option key={i} value={i}>{i.toString().padStart(2, '0')}:00</option>)}
                          </select>
                        </div>
                        <div className="form-group">
                          <label>Sleep End</label>
                          <select value={sleepEndHour} onChange={(e) => setSleepEndHour(+e.target.value)}>
                            {Array.from({length: 24}, (_, i) => <option key={i} value={i}>{i.toString().padStart(2, '0')}:00</option>)}
                          </select>
                        </div>
                        <div className="form-group">
                          <label>Blocks Order</label>
                          <select value={blocksOrderType} onChange={(e) => setBlocksOrderType(e.target.value as 'sequential' | 'random')}>
                            <option value="sequential">Sequential</option>
                            <option value="random">Random</option>
                          </select>
                        </div>
                        <div className="form-group">
                          <label>Sleep Variance (±min)</label>
                          <input type="number" value={sleepVariance} onChange={(e) => setSleepVariance(+e.target.value)} min="0" max="120" />
                        </div>
                        <div className="form-group">
                          <label>Block Duration (min)</label>
                          <input type="number" value={defaultBlockDuration} onChange={(e) => setDefaultBlockDuration(Math.max(5, +e.target.value))} min="5" max="480" />
                        </div>
                        <div className="form-group" style={{ display: 'flex', alignItems: 'flex-end' }}>
                          <button 
                            type="button" 
                            onClick={generateSleepSchedule} 
                            className="btn-primary" 
                            style={{ width: '100%' }}
                            disabled={generatingSleep || selectedRegions.length === 0}
                          >
                            {generatingSleep ? '⏳ Generating...' : '🌙 Generate Schedule'}
                          </button>
                        </div>
                      </div>
                      {selectedRegions.length === 0 && (
                        <p style={{ color: '#f87171', fontSize: '0.875rem', marginTop: '0.5rem' }}>
                          ⚠️ Please select a region in Step 1 first to determine timezone
                        </p>
                      )}
                    </>
                  ) : (
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '1rem' }}>
                      <div className="form-group">
                        <label>Total Duration (seconds)</label>
                        <input type="number" value={totalDuration} onChange={(e) => setTotalDuration(Number(e.target.value))} min={60} />
                        <small className="text-muted">{(totalDuration / 3600).toFixed(1)} hours</small>
                      </div>
                      <div className="form-group">
                        <label>Blocks Order</label>
                        <select value={blocksOrderType} onChange={(e) => setBlocksOrderType(e.target.value as 'sequential' | 'random')}>
                          <option value="sequential">Sequential</option>
                          <option value="random">Random</option>
                        </select>
                      </div>
                      <div className="form-group">
                        <label>Variance (%)</label>
                        <input type="number" value={variance} onChange={(e) => setVariance(Number(e.target.value))} min={0} max={50} />
                      </div>
                      <div className="form-group">
                        <label>Default Block Duration (min)</label>
                        <input type="number" value={defaultBlockDuration} onChange={(e) => setDefaultBlockDuration(Math.max(5, Number(e.target.value)))} min={5} max={480} />
                        <small className="text-muted">{(defaultBlockDuration / 60).toFixed(1)} hours</small>
                      </div>
                      <div className="form-group">
                        <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                          <input type="checkbox" checked={useQueue} onChange={(e) => setUseQueue(e.target.checked)} />
                          Schedule to Queue
                        </label>
                        <small className="text-muted">Send tasks to RabbitMQ instead of DB (workers must be running)</small>
                      </div>
                    </div>
                  )}
                </div>

                {/* Sleep Schedule Time Slots */}
                {useSleepSchedule && timeSlots.length > 0 && (
                  <div style={{ marginBottom: '1.5rem' }}>
                    {(() => {
                      const activitySlots = timeSlots.filter(s => s.type === 'activity');
                      const sleepSlots = timeSlots.filter(s => s.type === 'sleep');
                      const totalActivityTime = activitySlots.reduce((sum, s) => sum + s.durationSeconds, 0);
                      const totalScheduled = timeSlots.reduce((sum, s) => sum + s.activityBlocks.reduce((bs, b) => bs + b.block_duration_seconds, 0), 0);
                      const totalSleepTime = sleepSlots.reduce((sum, s) => sum + s.durationSeconds, 0);
                      const diff = totalActivityTime - totalScheduled;
                      const statusColor = Math.abs(diff) <= 60 ? '#4ade80' : diff < 0 ? '#f87171' : '#fbbf24';
                      return (
                        <div style={{
                          marginBottom: '1rem', padding: '12px 16px', borderRadius: '8px',
                          background: `${statusColor}15`, border: `1px solid ${statusColor}40`
                        }}>
                          <span style={{ color: statusColor, fontSize: '14px', fontWeight: '500' }}>
                            🌙 Sleep: {formatDuration(totalSleepTime)} &nbsp;|&nbsp;
                            ☀️ Activity: {formatDuration(totalScheduled)} / {formatDuration(totalActivityTime)}
                            {diff > 60 && ` (${formatDuration(diff)} remaining)`}
                            {diff < -60 && ` (${formatDuration(-diff)} over)`}
                            {Math.abs(diff) <= 60 && ' ✓'}
                          </span>
                        </div>
                      );
                    })()}
                    
                    {timeSlots.map((slot, slotIdx) => (
                      <div key={slotIdx} style={{
                        marginBottom: '1rem', padding: '1rem', borderRadius: '8px',
                        background: slot.type === 'sleep' ? 'rgba(139, 92, 246, 0.1)' : 'rgba(255,255,255,0.05)',
                        border: `1px solid ${slot.type === 'sleep' ? 'rgba(139, 92, 246, 0.3)' : 'rgba(255,255,255,0.1)'}`
                      }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: slot.type === 'activity' ? '0.75rem' : '0' }}>
                          <div>
                            <span style={{ fontSize: '1.25rem', marginRight: '0.5rem' }}>{slot.type === 'sleep' ? '🌙' : '☀️'}</span>
                            <strong style={{ color: slot.type === 'sleep' ? '#a78bfa' : 'inherit' }}>
                              {slot.type === 'sleep' ? 'Sleep' : 'Activity'} • {formatDuration(slot.durationSeconds)}
                            </strong>
                            <span className="text-muted" style={{ marginLeft: '1rem', fontSize: '0.875rem' }}>
                              {slot.localStart} → {slot.localEnd}
                            </span>
                          </div>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                            {slot.type === 'activity' && (() => {
                              const scheduled = slot.activityBlocks.reduce((sum, b) => sum + b.block_duration_seconds, 0);
                              const diff = slot.durationSeconds - scheduled;
                              const statusColor = Math.abs(diff) < 60 ? '#4ade80' : diff < 0 ? '#f87171' : '#fbbf24';
                              return (
                                <span style={{ fontSize: '0.875rem', color: statusColor }}>
                                  {formatDuration(scheduled)} / {formatDuration(slot.durationSeconds)}
                                  {diff > 60 && ` (${formatDuration(diff)} left)`}
                                </span>
                              );
                            })()}
                            {slot.type === 'sleep' && (
                              <button type="button" onClick={() => removeSleepSlot(slotIdx)} className="btn-secondary" style={{ padding: '4px 8px', fontSize: '0.75rem' }}>
                                Remove
                              </button>
                            )}
                          </div>
                        </div>
                        
                        {slot.type === 'activity' && (
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                            {slot.activityBlocks.map((block, blockIdx) => (
                              <div key={blockIdx} style={{ background: 'rgba(0,0,0,0.2)', borderRadius: '6px', padding: '0.75rem' }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
                                  <span style={{ fontWeight: '500' }}>Block {blockIdx + 1}</span>
                                  <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'center' }}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                                      <label style={{ fontSize: '0.75rem' }}>Tasks:</label>
                                      <select
                                        value={block.tasks_order_type}
                                        onChange={(e) => updateBlockInSlot(slotIdx, blockIdx, { tasks_order_type: e.target.value as 'sequential' | 'random' })}
                                        style={{ padding: '4px', fontSize: '0.75rem' }}
                                      >
                                        <option value="sequential">Sequential</option>
                                        <option value="random">Random</option>
                                      </select>
                                    </div>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                                      <label style={{ fontSize: '0.75rem' }}>Duration:</label>
                                      <input
                                        type="number"
                                        value={Math.floor(block.block_duration_seconds / 60)}
                                        onChange={(e) => updateBlockInSlot(slotIdx, blockIdx, { block_duration_seconds: +e.target.value * 60 })}
                                        style={{ width: '60px', padding: '4px' }}
                                        min="1"
                                      />
                                      <span style={{ fontSize: '0.75rem' }}>min</span>
                                    </div>
                                    <button type="button" onClick={() => removeBlockFromSlot(slotIdx, blockIdx)} className="btn-secondary" style={{ padding: '4px 8px', fontSize: '0.75rem' }}>✕</button>
                                  </div>
                                </div>
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                                  {block.scheduled_tasks.map((task, taskIdx) => (
                                    <div key={taskIdx} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', background: 'rgba(255,255,255,0.05)', padding: '0.5rem', borderRadius: '4px' }}>
                                      <select
                                        value={task.task_id}
                                        onChange={(e) => updateTaskInSlotBlock(slotIdx, blockIdx, taskIdx, { task_id: +e.target.value })}
                                        style={{ flex: 1, padding: '4px' }}
                                      >
                                        {availableTasks.map(t => (
                                          <option key={t.task_id} value={t.task_id}>
                                            {t.task_type === 'sleep' ? '🌙' : t.task_type === 'search' ? '🔍' : '📱'} {t.task_type}
                                            {t.task_params?.search_keyword ? ` (${t.task_params.search_keyword})` : ''}
                                          </option>
                                        ))}
                                      </select>
                                      <input
                                        type="number"
                                        value={task.total_duration_seconds ? Math.floor(task.total_duration_seconds / 60) : ''}
                                        onChange={(e) => {
                                          const minutes = Number(e.target.value);
                                          updateTaskInSlotBlock(slotIdx, blockIdx, taskIdx, {
                                            total_duration_seconds: minutes > 0 ? Math.floor(minutes) * 60 : undefined,
                                          });
                                        }}
                                        placeholder="total"
                                        style={{ width: '58px', padding: '4px' }}
                                        min="1"
                                      />
                                      <span style={{ fontSize: '0.75rem' }}>min</span>
                                      <input type="number" value={task.min_duration_seconds ? Math.floor(task.min_duration_seconds / 60) : ''} onChange={(e) => updateTaskInSlotBlock(slotIdx, blockIdx, taskIdx, { min_duration_seconds: +e.target.value * 60 })} placeholder="min" style={{ width: '50px', padding: '4px' }} />
                                      <span>-</span>
                                      <input type="number" value={task.max_duration_seconds ? Math.floor(task.max_duration_seconds / 60) : ''} onChange={(e) => updateTaskInSlotBlock(slotIdx, blockIdx, taskIdx, { max_duration_seconds: +e.target.value * 60 })} placeholder="max" style={{ width: '50px', padding: '4px' }} />
                                      <span style={{ fontSize: '0.75rem' }}>min</span>
                                      <button type="button" onClick={() => removeTaskFromSlotBlock(slotIdx, blockIdx, taskIdx)} style={{ background: 'transparent', border: 'none', color: '#f87171', cursor: 'pointer' }}>×</button>
                                    </div>
                                  ))}
                                  <button type="button" onClick={() => addTaskToSlotBlock(slotIdx, blockIdx)} style={{ background: 'rgba(34, 197, 94, 0.1)', border: '1px dashed rgba(34, 197, 94, 0.4)', color: '#4ade80', padding: '6px', borderRadius: '4px', fontSize: '0.875rem', cursor: 'pointer' }}>
                                    + Add Task
                                  </button>
                                </div>
                              </div>
                            ))}
                            <button type="button" onClick={() => addBlockToSlot(slotIdx)} style={{ background: 'rgba(34, 197, 94, 0.1)', border: '2px dashed rgba(34, 197, 94, 0.4)', color: '#4ade80', padding: '12px', borderRadius: '6px', fontSize: '0.875rem', cursor: 'pointer', width: '100%' }}>
                              + Add Block
                            </button>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}

                {/* Original Manual Schedule - only show if not using sleep schedule */}
                {!useSleepSchedule && (
                  <>
                    {/* Schedule Summary */}
                    {(() => {
                      const totalScheduled = scheduleBlocks.reduce((sum, b) => sum + b.block_duration_seconds, 0);
                      const diff = totalDuration - totalScheduled;
                      const isOver = totalScheduled > totalDuration;
                      const isUnder = totalScheduled < totalDuration;
                      const isMatch = Math.abs(diff) < 60;
                      const statusColor = isMatch ? '#4ade80' : isOver ? '#f87171' : '#fbbf24';
                      return (
                        <div style={{
                          marginBottom: '1.5rem', padding: '12px 16px', borderRadius: '8px',
                          background: `${statusColor}15`, border: `1px solid ${statusColor}40`,
                          display: 'flex', justifyContent: 'space-between', alignItems: 'center'
                        }}>
                          <span style={{ color: statusColor, fontSize: '14px', fontWeight: '500' }}>
                            {isMatch ? '✓' : isOver ? '⚠️' : '📊'} Scheduled: {formatDuration(totalScheduled)} / {formatDuration(totalDuration)}
                            {isUnder && !isMatch && ` (${formatDuration(diff)} remaining)`}
                            {isOver && ` (${formatDuration(-diff)} over)`}
                          </span>
                          <span style={{ color: 'var(--text-muted)', fontSize: '13px' }}>
                            {scheduleBlocks.length} block{scheduleBlocks.length !== 1 ? 's' : ''}
                          </span>
                    </div>
                  );
                })()}

                {/* Blocks */}
                {scheduleBlocks.map((block, blockIdx) => (
                  <div key={blockIdx} className="card" style={{ marginBottom: '1.5rem', padding: '1rem', border: '2px solid var(--border-default)' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
                      <h3>Block {blockIdx + 1}</h3>
                      <button 
                        type="button"
                        onClick={() => setScheduleBlocks(scheduleBlocks.filter((_, i) => i !== blockIdx))}
                        className="btn-secondary"
                        disabled={scheduleBlocks.length === 1}
                      >
                        Remove Block
                      </button>
                    </div>

                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1rem' }}>
                      <div className="form-group">
                        <label>Block Duration (seconds)</label>
                        <input 
                          type="number"
                          value={block.block_duration_seconds}
                          onChange={(e) => {
                            const newBlocks = [...scheduleBlocks];
                            newBlocks[blockIdx].block_duration_seconds = Number(e.target.value);
                            setScheduleBlocks(newBlocks);
                          }}
                          min={60}
                        />
                      </div>
                      <div className="form-group">
                        <label>Tasks Order</label>
                        <select 
                          value={block.tasks_order_type}
                          onChange={(e) => {
                            const newBlocks = [...scheduleBlocks];
                            newBlocks[blockIdx].tasks_order_type = e.target.value as 'sequential' | 'random';
                            setScheduleBlocks(newBlocks);
                          }}
                        >
                          <option value="sequential">Sequential</option>
                          <option value="random">Random</option>
                        </select>
                      </div>
                    </div>

                    {/* Tasks in Block */}
                    <div style={{ marginTop: '1rem' }}>
                      {block.scheduled_tasks.map((task, taskIdx) => {
                        const taskInfo = availableTasks.find(t => t.task_id === task.task_id);
                        return (
                          <div key={taskIdx} style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: '8px', padding: '12px', marginBottom: '8px' }}>
                            <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr 1fr 1fr auto', gap: '12px', alignItems: 'center' }}>
                              <select
                                value={task.task_id}
                                onChange={(e) => {
                                  const newBlocks = [...scheduleBlocks];
                                  newBlocks[blockIdx].scheduled_tasks[taskIdx].task_id = Number(e.target.value);
                                  setScheduleBlocks(newBlocks);
                                }}
                                className="persona-input"
                              >
                                {availableTasks.map(t => (
                                  <option key={t.task_id} value={t.task_id}>
                                    {t.task_type} {t.task_params ? `- ${JSON.stringify(t.task_params)}` : ''}
                                  </option>
                                ))}
                              </select>

                              <input
                                type="number"
                                value={task.total_duration_seconds || ''}
                                onChange={(e) => {
                                  const newBlocks = [...scheduleBlocks];
                                  const seconds = Number(e.target.value);
                                  newBlocks[blockIdx].scheduled_tasks[taskIdx].total_duration_seconds = seconds > 0 ? Math.floor(seconds) : undefined;
                                  setScheduleBlocks(newBlocks);
                                }}
                                placeholder="Total in block (s)"
                                className="persona-input"
                                min={1}
                              />

                              <input
                                type="number"
                                value={task.duration_seconds || ''}
                                onChange={(e) => {
                                  const newBlocks = [...scheduleBlocks];
                                  newBlocks[blockIdx].scheduled_tasks[taskIdx].duration_seconds = e.target.value ? Number(e.target.value) : undefined;
                                  newBlocks[blockIdx].scheduled_tasks[taskIdx].min_duration_seconds = undefined;
                                  newBlocks[blockIdx].scheduled_tasks[taskIdx].max_duration_seconds = undefined;
                                  setScheduleBlocks(newBlocks);
                                }}
                                placeholder="Duration (s)"
                                className="persona-input"
                              />

                              <input
                                type="number"
                                value={task.min_duration_seconds || ''}
                                onChange={(e) => {
                                  const newBlocks = [...scheduleBlocks];
                                  newBlocks[blockIdx].scheduled_tasks[taskIdx].min_duration_seconds = e.target.value ? Number(e.target.value) : undefined;
                                  newBlocks[blockIdx].scheduled_tasks[taskIdx].duration_seconds = undefined;
                                  setScheduleBlocks(newBlocks);
                                }}
                                placeholder="Min (s)"
                                className="persona-input"
                                disabled={!!task.duration_seconds}
                              />

                              <input
                                type="number"
                                value={task.max_duration_seconds || ''}
                                onChange={(e) => {
                                  const newBlocks = [...scheduleBlocks];
                                  newBlocks[blockIdx].scheduled_tasks[taskIdx].max_duration_seconds = e.target.value ? Number(e.target.value) : undefined;
                                  newBlocks[blockIdx].scheduled_tasks[taskIdx].duration_seconds = undefined;
                                  setScheduleBlocks(newBlocks);
                                }}
                                placeholder="Max (s)"
                                className="persona-input"
                                disabled={!!task.duration_seconds}
                              />

                              <button 
                                type="button"
                                onClick={() => {
                                  const newBlocks = [...scheduleBlocks];
                                  newBlocks[blockIdx].scheduled_tasks = newBlocks[blockIdx].scheduled_tasks.filter((_, i) => i !== taskIdx);
                                  setScheduleBlocks(newBlocks);
                                }}
                                className="btn-secondary"
                                style={{ padding: '0.5rem' }}
                              >
                                ×
                              </button>
                            </div>
                          </div>
                        );
                      })}
                      
                      <button 
                        type="button"
                        onClick={() => {
                          const newBlocks = [...scheduleBlocks];
                          newBlocks[blockIdx].scheduled_tasks.push({
                            task_id: availableTasks[0].task_id,
                            min_duration_seconds: 300,
                            max_duration_seconds: 600,
                            number_of_tasks: 1
                          });
                          setScheduleBlocks(newBlocks);
                        }}
                        className="btn-primary"
                        style={{ width: '100%', marginTop: '0.5rem' }}
                      >
                        + Add Task to Block
                      </button>
                    </div>
                  </div>
                ))}

                    <button 
                      type="button"
                      onClick={() => setScheduleBlocks([...scheduleBlocks, {
                        scheduled_tasks: [],
                        block_duration_seconds: defaultBlockDuration * 60,
                        tasks_order_type: 'sequential'
                      }])}
                      className="btn-primary"
                      style={{ width: '100%' }}
                    >
                      + Add Block
                    </button>
                  </>
                )}
              </>
            )}
          </div>
        );

      case 6:
        return (
          <div className="step-content">
            <h2>Review & Deploy</h2>
            <p className="text-muted">Review your configuration and deploy the bot group</p>
            
            <div className="form-group" style={{ marginTop: '2rem' }}>
              <label className="form-label">Bot Group Name</label>
              <input
                type="text"
                value={groupName}
                onChange={e => setGroupName(e.target.value)}
                placeholder="Enter a name for this bot group"
              />
            </div>

            <div className="card" style={{ marginTop: '2rem', padding: '1.5rem' }}>
              <h3>Experiment Assignment</h3>
              <p className="text-muted" style={{ marginBottom: '1rem' }}>
                Assign this bot group to an experiment
              </p>
              
              <div style={{ display: 'flex', gap: '1rem', marginBottom: '1rem' }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
                  <input
                    type="radio"
                    checked={experimentMode === 'new'}
                    onChange={() => setExperimentMode('new')}
                    style={{ width: '18px', height: '18px' }}
                  />
                  <span>Create New Experiment</span>
                </label>
                <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
                  <input
                    type="radio"
                    checked={experimentMode === 'existing'}
                    onChange={() => setExperimentMode('existing')}
                    style={{ width: '18px', height: '18px' }}
                  />
                  <span>Add to Existing Experiment</span>
                </label>
              </div>

              {experimentMode === 'new' && (
                <div className="form-group">
                  <label className="form-label">Experiment Name</label>
                  <input
                    type="text"
                    value={experimentName}
                    onChange={e => setExperimentName(e.target.value)}
                    placeholder="Enter experiment name"
                    style={{ width: '100%' }}
                  />
                  <p className="text-muted" style={{ fontSize: '0.875rem', marginTop: '0.5rem' }}>
                    Leave empty to use default: "{groupName} Experiment"
                  </p>
                </div>
              )}

              {experimentMode === 'existing' && (
                <div className="form-group">
                  <label className="form-label">Select Experiment</label>
                  <select
                    value={selectedExperiment?.experimentId || ''}
                    onChange={e => {
                      const exp = experiments.find(ex => ex.experimentId === Number(e.target.value));
                      setSelectedExperiment(exp || null);
                    }}
                    style={{ width: '100%' }}
                  >
                    <option value="">-- Select an experiment --</option>
                    {experiments.map(exp => (
                      <option key={exp.experimentId} value={exp.experimentId}>
                        {exp.experimentName} (ID: {exp.experimentId})
                      </option>
                    ))}
                  </select>
                  {experiments.length === 0 && (
                    <p className="text-muted" style={{ fontSize: '0.875rem', marginTop: '0.5rem' }}>
                      No experiments available. Switch to "Create New Experiment" mode.
                    </p>
                  )}
                </div>
              )}
            </div>

            <div className="card" style={{ marginTop: '2rem' }}>
              <h3>Configuration Summary</h3>
              <div style={{ marginTop: '1rem' }}>
                <div style={{ padding: '0.75rem', borderBottom: '1px solid var(--border-default)' }}>
                  <strong>Regions:</strong> {selectedRegions.length} region{selectedRegions.length !== 1 ? 's' : ''} (random assignment)
                  <div className="text-muted" style={{ fontSize: '0.8rem', marginTop: '0.25rem' }}>
                    {selectedRegions.map(r => r.carrierRegion.toUpperCase()).join(', ')}
                  </div>
                </div>
                <div style={{ padding: '0.75rem', borderBottom: '1px solid var(--border-default)' }}>
                  <strong>Devices:</strong> {selectedDevices.length} device{selectedDevices.length !== 1 ? 's' : ''} (random assignment)
                  <div className="text-muted" style={{ fontSize: '0.8rem', marginTop: '0.25rem' }}>
                    {selectedDevices.map(d => `${d.deviceBrand} ${d.deviceType}`).join(', ')}
                  </div>
                </div>
                <div style={{ padding: '0.75rem', borderBottom: '1px solid var(--border-default)' }}>
                  <strong>Personas:</strong> {personasToCreate} bots with unique interests
                </div>
                {proxyConfig.enabled && (
                  <div style={{ padding: '0.75rem', borderBottom: '1px solid var(--border-default)' }}>
                    <strong>Proxy:</strong> {
                      proxyConfig.proxyId 
                        ? `Existing proxy ID: ${proxyConfig.proxyId}` 
                        : proxyConfig.createMode === 'brightdata' 
                          ? `New Bright Data ISP: ${proxyConfig.brightDataProxyName} (${proxyConfig.brightDataCountry}, ${proxyConfig.brightDataIpCount} IP${proxyConfig.brightDataIpCount !== 1 ? 's' : ''})`
                          : proxyConfig.newProxyUrl || 'Not configured'
                    }
                  </div>
                )}
                <div style={{ padding: '0.75rem', borderBottom: '1px solid var(--border-default)' }}>
                  <strong>Classifier:</strong> {selectedClassifier?.classifierType}
                </div>
                <div style={{ padding: '0.75rem', borderBottom: '1px solid var(--border-default)' }}>
                  <strong>Narratives:</strong> {selectedNarratives.length > 0 
                    ? `${selectedNarratives.length} narrative${selectedNarratives.length !== 1 ? 's' : ''} (${selectedNarratives.map(id => narratives.find(n => n.narrativeId === id)?.name || id).join(', ')})`
                    : 'None selected'}
                </div>
                <div style={{ padding: '0.75rem', borderBottom: '1px solid var(--border-default)' }}>
                  <strong>Experiment:</strong> {experimentMode === 'new' 
                    ? `New: "${experimentName || `${groupName} Experiment`}"` 
                    : selectedExperiment 
                      ? `Add to "${selectedExperiment.experimentName}" (ID: ${selectedExperiment.experimentId})`
                      : 'No experiment selected'}
                </div>
                <div style={{ padding: '0.75rem' }}>
                  <strong>Schedule:</strong> {scheduleBlocks.some(block => block.scheduled_tasks.length > 0)
                    ? `${scheduleBlocks.length} block(s), ${(totalDuration / 3600).toFixed(1)}h total, ${blocksOrderType} order${useQueue ? ' (queue)' : ''}`
                    : 'No tasks scheduled (can be added later)'}
                </div>
              </div>
            </div>

            {error && (
              <div className="error" style={{ marginTop: '1rem' }}>
                {error}
              </div>
            )}
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="wizard-container">
      {/* Global Error Banner */}
      {error && (
        <div className="error" style={{ marginBottom: '1rem' }}>
          {error}
          <button 
            onClick={() => setError(null)} 
            style={{ 
              float: 'right', 
              background: 'none', 
              border: 'none', 
              color: 'inherit', 
              fontSize: '1.2rem', 
              cursor: 'pointer',
              padding: '0 0.5rem'
            }}
          >
            ×
          </button>
        </div>
      )}

      {/* Progress Steps */}
      <div className="wizard-steps">
        {STEPS.map(step => (
          <div
            key={step.id}
            className={`wizard-step ${currentStep === step.id ? 'active' : ''} ${currentStep > step.id ? 'completed' : ''}`}
            onClick={() => {
              if (step.id < currentStep) {
                setCurrentStep(step.id);
                setError(null);
              }
            }}
          >
            <div className="step-number">{step.id}</div>
            <div className="step-info">
              <div className="step-title">{step.title}</div>
              <div className="step-description">{step.description}</div>
            </div>
          </div>
        ))}
      </div>

      {/* Step Content */}
      <div className="wizard-content">
        {renderStepContent()}
      </div>

      {/* Navigation */}
      <div className="wizard-navigation">
        <button
          className="btn btn-secondary"
          onClick={() => setCurrentStep(Math.max(1, currentStep - 1))}
          disabled={currentStep === 1}
        >
          ← Previous
        </button>
        
        <div style={{ flex: 1 }}></div>

        {currentStep < 6 ? (
          <button
            className="btn btn-primary"
            onClick={() => setCurrentStep(currentStep + 1)}
            disabled={!canProceed()}
          >
            Next →
          </button>
        ) : (
          <button
            className="btn btn-success"
            onClick={handleDeploy}
            disabled={!canProceed() || loading}
          >
            {loading ? 'Deploying...' : 'Deploy Bot Group'}
          </button>
        )}
      </div>
    </div>
  );
};

export default BotGroupWizard;

